import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "chart.js/auto";

import FilterPatientPopUp from "../../components/PopUp/FilterPatientPopUp";
import EcrfDiscrepancyPopUp from "../../components/PopUp/EcrfDiscrepancyPopUp";
import ExportStudyReportModal from "../../components/PopUp/ExportStudyReportModal";
import EcrfDiscrepancySection from "../../components/Trials/EcrfDiscrepancySection";
import logUserAction from "../../apis/userLogger";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import useGetSummaryTrial from "../../hooks/useGetSummaryTrial";
import useGetEcrfDiscrepancySummary from "../../hooks/useGetEcrfDiscrepancySummary";
import { USER_ROLES } from "../../utils/constants";
import CustomButton from "../../components/UI/CustomButton";
import Loader from "../../components/Loader";
import useDeleteTrialMutation from "../../hooks/useDeleteTrialMutation";
import useGetTrialCompletionPreview from "../../hooks/useGetTrialCompletionPreview";
import useMarkTrialCompleteMutation from "../../hooks/useMarkTrialCompleteMutation";
import useMarkTrialActiveMutation from "../../hooks/useMarkTrialActiveMutation";
import useArchiveTrialMutation from "../../hooks/useArchiveTrialMutation";
import useGetArchiveStatus from "../../hooks/useGetArchiveStatus";
import useUnarchiveTrialMutation from "../../hooks/useUnarchiveTrialMutation";
import useFinalizeArchiveTrialMutation from "../../hooks/useFinalizeArchiveTrialMutation";
import {
  generateTrialArchiveEcrfZip,
  uploadTrialArchiveEcrfZip,
} from "../../utils/archiveEcrfZipUtils";
import { showApiErrorToast } from "../../utils/helpers";

dayjs.extend(utc);

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[3],
  transition: "box-shadow 0.3s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
}));

const STATUS_CHIP_COLORS = {
  Active: "success",
  Completed: "primary",
  Archived: "default",
};

const KPI_COLORS = {
  "Total Subjects Enrolled": "#6B7280",
  "Pre-Screening": "#F59E0B",
  "Screening Failed": "#3B82F6",
  "Continuing (Active)": "#4ADE80",
  Withdrew: "#92400E",
  Terminated: "#EF4444",
  "All Visits Completed": "#166534",
  "Target Enrollment": "#6B7280",
  "eCRF Discrepancies": "#DC2626",
};

const CHART_COLORS = [
  "#F59E0B", // Pre-Screening
  "#3B82F6", // Screening Failed
  "#4ADE80", // Continuing (Active)
  "#92400E", // Withdrew
  "#EF4444", // Terminated
  "#166534", // All Visits Completed
];

const SummaryTrial = () => {
  const navigate = useNavigate();
  const { _id_ } = useParams();
  const queryClient = useQueryClient();

  const [filterPatientPopUp, setFilterPatientPopUp] = useState(false);
  const [filterPatient, setFilterPatient] = useState({});
  const [ecrfDiscrepancyPopUp, setEcrfDiscrepancyPopUp] = useState(false);
  const [ecrfDiscrepancyData, setEcrfDiscrepancyData] = useState(null);
  const [openConfirmDeleteTrialDialog, setOpenConfirmDeleteTrialDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openMarkActiveDialog, setOpenMarkActiveDialog] = useState(false);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [openUnarchiveDialog, setOpenUnarchiveDialog] = useState(false);
  const [openExportReportModal, setOpenExportReportModal] = useState(false);
  const [archiveTaskId, setArchiveTaskId] = useState(null);
  const [reportsArchiveState, setReportsArchiveState] = useState(null); // null | "pending" | "success" | "failure"
  const [ecrfArchiveState, setEcrfArchiveState] = useState(null); // null | "pending" | "success" | "failure"
  const [ecrfArchiveProgress, setEcrfArchiveProgress] = useState({ current: 0, total: 0 });
  const [ecrfArchiveFailedVisits, setEcrfArchiveFailedVisits] = useState([]);
  const [selectedIncompleteStatus, setSelectedIncompleteStatus] = useState("");

  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const isSuperAdmin = user_data.role === USER_ROLES.SUPER_ADMIN;
  const isCro = user_data.role === USER_ROLES.CRO;
  const isLifecycleAdmin = isSuperAdmin || isCro;
  const isDataManager = user_data.role === USER_ROLES.DATA_MANAGER;
  const canExportStudyReport = isSuperAdmin || isDataManager;

  const deleteTrialMutation = useDeleteTrialMutation();
  const markCompleteMutation = useMarkTrialCompleteMutation();
  const markActiveMutation = useMarkTrialActiveMutation();
  const archiveMutation = useArchiveTrialMutation();
  const unarchiveMutation = useUnarchiveTrialMutation();
  const finalizeArchiveMutation = useFinalizeArchiveTrialMutation();

  const { data: summaryTrial = {}, isLoading: isSummaryTrialLoading } = useGetSummaryTrial({
    trialId: _id_,
  });

  const {
    sites: discrepancySites,
    isLoading: isDiscrepancyLoading,
    isError: isDiscrepancyError,
  } = useGetEcrfDiscrepancySummary({
    trialId: _id_,
    enabled: !isSummaryTrialLoading && Boolean(summaryTrial?.has_trial_id_setting),
  });

  const {
    refetch: fetchPreview,
    data: previewData,
    isFetching: isPreviewFetching,
  } = useGetTrialCompletionPreview({ trialId: _id_ });

  const { data: archiveStatus } = useGetArchiveStatus({
    trialId: _id_,
    taskId: archiveTaskId,
  });

  const trialStatus = summaryTrial?.status;
  const isArchiving = Boolean(archiveTaskId);

  useEffect(() => {
    if (!archiveStatus) return;
    if (archiveStatus.state === "SUCCESS") {
      setReportsArchiveState("success");
    } else if (archiveStatus.state === "FAILURE") {
      setReportsArchiveState("failure");
      toast.error("Generating backend archive reports failed. Please try again.");
    }
  }, [archiveStatus]);

  useEffect(() => {
    if (reportsArchiveState !== "success" || ecrfArchiveState !== "success" || !archiveTaskId)
      return;

    finalizeArchiveMutation.mutate(
      { trialId: _id_, taskId: archiveTaskId, ecrfFailedVisits: ecrfArchiveFailedVisits },
      {
        onSuccess: () => {
          setArchiveTaskId(null);
          setReportsArchiveState(null);
          setEcrfArchiveState(null);
          toast.success("Trial archived successfully.");
          queryClient.invalidateQueries({ queryKey: ["summary_trial", _id_] });
          queryClient.invalidateQueries({ queryKey: ["trials_list"] });
        },
        onError: (error) => {
          showApiErrorToast(error, "Failed to finalize archive.");
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsArchiveState, ecrfArchiveState]);

  useEffect(() => {
    if (!isArchiving) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isArchiving]);

  const runEcrfArchiveZip = async (trialId) => {
    const token = localStorage.getItem("token");
    setEcrfArchiveState("pending");
    setEcrfArchiveProgress({ current: 0, total: 0 });
    try {
      const { zipBlob, failedVisits } = await generateTrialArchiveEcrfZip({
        trialId,
        token,
        onProgress: setEcrfArchiveProgress,
      });
      setEcrfArchiveFailedVisits(failedVisits);
      if (zipBlob) {
        await uploadTrialArchiveEcrfZip({ trialId, zipBlob, token });
      }
      setEcrfArchiveState("success");
    } catch (error) {
      setEcrfArchiveState("failure");
      showApiErrorToast(error, "Failed to generate eCRF PDF archive.");
    }
  };

  const handleConfirmDeletePatients = () => {
    deleteTrialMutation.mutate(
      { trialId: _id_ },
      {
        onSuccess: () => {
          setOpenConfirmDeleteTrialDialog(false);
          navigate("/clinic_trials/");
        },
      }
    );
    logUserAction("Click on Delete Trial Button");
  };

  const handleShowFilterPatientPopUp = (data) => {
    setFilterPatientPopUp(true);
    setFilterPatient(data);
  };

  const handleShowEcrfDiscrepancyPopUp = (data) => {
    setEcrfDiscrepancyPopUp(true);
    setEcrfDiscrepancyData(data);
  };

  const handleMarkCompleteClick = async () => {
    const result = await fetchPreview();
    if (result.data !== undefined) {
      setSelectedIncompleteStatus("");
      setOpenPreviewDialog(true);
    }
  };

  const handleConfirmMarkComplete = () => {
    markCompleteMutation.mutate(
      { trialId: _id_, incompletePatientStatus: selectedIncompleteStatus || "Completed" },
      {
        onSuccess: () => {
          setOpenPreviewDialog(false);
          toast.success("Trial locked.");
        },
        onError: (error) => {
          showApiErrorToast(error, "Failed to lock trial.");
        },
      }
    );
    logUserAction("Click on Lock Trial Button");
  };

  const handleConfirmMarkActive = () => {
    markActiveMutation.mutate(
      { trialId: _id_ },
      {
        onSuccess: (data) => {
          setOpenMarkActiveDialog(false);
          toast.success("Trial unlocked.");
          if (data?.warning) {
            toast.warning(data.warning);
          }
        },
        onError: (error) => {
          setOpenMarkActiveDialog(false);
          showApiErrorToast(error, "Failed to unlock trial.");
        },
      }
    );
    logUserAction("Click on Unlock Trial Button");
  };

  const handleConfirmArchive = () => {
    archiveMutation.mutate(
      { trialId: _id_ },
      {
        onSuccess: (data) => {
          setOpenArchiveDialog(false);
          setArchiveTaskId(data.task_id);
          setReportsArchiveState("pending");
          setEcrfArchiveFailedVisits([]);
          runEcrfArchiveZip(_id_);
        },
        onError: (error) => {
          setOpenArchiveDialog(false);
          showApiErrorToast(error, "Failed to initiate archive.");
        },
      }
    );
    logUserAction("Click on Archive Trial Button");
  };

  const handleConfirmUnarchive = () => {
    unarchiveMutation.mutate(
      { trialId: _id_ },
      {
        onSuccess: () => {
          setOpenUnarchiveDialog(false);
          toast.success("Trial unarchived and set back to Completed.");
        },
        onError: (error) => {
          setOpenUnarchiveDialog(false);
          showApiErrorToast(error, "Failed to unarchive trial.");
        },
      }
    );
    logUserAction("Click on Unarchive Trial Button");
  };

  const getPatientData = (site) => [
    {
      label: "Total Subjects Enrolled",
      count: site?.patients_count_dicts?.total_subjects_enrolled?.length || 0,
      data: site?.patients_count_dicts?.total_subjects_enrolled,
      clickable: true,
    },
    {
      label: "Pre-Screening",
      count: site?.patients_count_dicts?.pre_screening_subjects?.length || 0,
      data: site?.patients_count_dicts?.pre_screening_subjects,
      clickable: true,
    },
    {
      label: "Screening Failed",
      count: site?.patients_count_dicts?.screening_failed_subjects?.length || 0,
      data: site?.patients_count_dicts?.screening_failed_subjects,
      clickable: true,
    },
    {
      label: "Continuing (Active)",
      count: site?.patients_count_dicts?.continuing_subjects?.length || 0,
      data: site?.patients_count_dicts?.continuing_subjects,
      clickable: true,
    },
    {
      label: "Withdrew",
      count: site?.patients_count_dicts?.withdrew_subjects?.length || 0,
      data: site?.patients_count_dicts?.withdrew_subjects,
      clickable: true,
    },
    {
      label: "Terminated",
      count: site?.patients_count_dicts?.terminated_subjects?.length || 0,
      data: site?.patients_count_dicts?.terminated_subjects,
      clickable: true,
    },
    {
      label: "All Visits Completed",
      count: site?.patients_count_dicts?.all_visits_completed_subjects?.length || 0,
      data: site?.patients_count_dicts?.all_visits_completed_subjects,
      clickable: true,
    },
    {
      label: "Target Enrollment",
      count: site?.target_subject_enrollment || 0,
      data: null,
      clickable: false,
    },
  ];

  const getOverallLastEnrolled = () => {
    if (!summaryTrial?.sites?.length) return null;
    const entries = summaryTrial.sites.map((s) => s.last_subject_enrolled_date).filter(Boolean);
    if (!entries.length) return null;
    return entries.reduce((latest, entry) => (entry.date > latest.date ? entry : latest));
  };

  const getOverallKPIs = () => {
    if (!summaryTrial?.sites?.length) return [];

    const aggregatedData = {
      total_subjects_enrolled: [],
      pre_screening_subjects: [],
      screening_failed_subjects: [],
      continuing_subjects: [],
      withdrew_subjects: [],
      terminated_subjects: [],
      all_visits_completed_subjects: [],
      target_subject_enrollment: 0,
    };

    summaryTrial.sites.forEach((site) => {
      if (site?.patients_count_dicts?.total_subjects_enrolled) {
        aggregatedData.total_subjects_enrolled.push(
          ...site.patients_count_dicts.total_subjects_enrolled
        );
      }
      if (site?.patients_count_dicts?.pre_screening_subjects) {
        aggregatedData.pre_screening_subjects.push(
          ...site.patients_count_dicts.pre_screening_subjects
        );
      }
      if (site?.patients_count_dicts?.screening_failed_subjects) {
        aggregatedData.screening_failed_subjects.push(
          ...site.patients_count_dicts.screening_failed_subjects
        );
      }
      if (site?.patients_count_dicts?.continuing_subjects) {
        aggregatedData.continuing_subjects.push(...site.patients_count_dicts.continuing_subjects);
      }
      if (site?.patients_count_dicts?.withdrew_subjects) {
        aggregatedData.withdrew_subjects.push(...site.patients_count_dicts.withdrew_subjects);
      }
      if (site?.patients_count_dicts?.terminated_subjects) {
        aggregatedData.terminated_subjects.push(...site.patients_count_dicts.terminated_subjects);
      }
      if (site?.patients_count_dicts?.all_visits_completed_subjects) {
        aggregatedData.all_visits_completed_subjects.push(
          ...site.patients_count_dicts.all_visits_completed_subjects
        );
      }

      if (!aggregatedData.target_subject_enrollment) {
        aggregatedData.target_subject_enrollment += site?.target_subject_enrollment || 0;
      }
    });

    return [
      {
        label: "Total Subjects Enrolled",
        count: aggregatedData.total_subjects_enrolled.length,
        data: aggregatedData.total_subjects_enrolled,
        clickable: false,
      },
      {
        label: "Pre-Screening",
        count: aggregatedData.pre_screening_subjects.length,
        data: aggregatedData.pre_screening_subjects,
        clickable: false,
      },
      {
        label: "Screening Failed",
        count: aggregatedData.screening_failed_subjects.length,
        data: aggregatedData.screening_failed_subjects,
        clickable: false,
      },
      {
        label: "Continuing (Active)",
        count: aggregatedData.continuing_subjects.length,
        data: aggregatedData.continuing_subjects,
        clickable: false,
      },
      {
        label: "Withdrew",
        count: aggregatedData.withdrew_subjects.length,
        data: aggregatedData.withdrew_subjects,
        clickable: false,
      },
      {
        label: "Terminated",
        count: aggregatedData.terminated_subjects.length,
        data: aggregatedData.terminated_subjects,
        clickable: false,
      },
      {
        label: "All Visits Completed",
        count: aggregatedData.all_visits_completed_subjects.length,
        data: aggregatedData.all_visits_completed_subjects,
        clickable: false,
      },
      {
        label: "Target Enrollment",
        count: aggregatedData.target_subject_enrollment,
        data: null,
        clickable: false,
      },
    ];
  };

  const getSiteEcrfDiscrepancies = (site) => discrepancySites?.[site?.id] || null;

  const getOverallEcrfDiscrepancies = () => {
    const byType = { date_mismatch: 0, partially_filled: 0, out_of_range: 0 };
    const patients = [];

    Object.values(discrepancySites || {}).forEach((siteSummary) => {
      const siteByType = siteSummary?.by_type;
      if (siteByType) {
        byType.date_mismatch += siteByType.date_mismatch || 0;
        byType.partially_filled += siteByType.partially_filled || 0;
        byType.out_of_range += siteByType.out_of_range || 0;
      }
      if (siteSummary?.patients?.length) {
        patients.push(...siteSummary.patients);
      }
    });

    return {
      total: byType.date_mismatch + byType.partially_filled + byType.out_of_range,
      by_type: byType,
      patients,
    };
  };

  const getChartData = (site) => ({
    labels: [
      "Pre-Screening",
      "Screening Failed",
      "Continuing (Active)",
      "Withdrew",
      "Terminated",
      "All Visits Completed",
    ],
    datasets: [
      {
        backgroundColor: CHART_COLORS,
        data: [
          site?.patients_count_dicts?.pre_screening_subjects?.length || 0,
          site?.patients_count_dicts?.screening_failed_subjects?.length || 0,
          site?.patients_count_dicts?.continuing_subjects?.length || 0,
          site?.patients_count_dicts?.withdrew_subjects?.length || 0,
          site?.patients_count_dicts?.terminated_subjects?.length || 0,
          site?.patients_count_dicts?.all_visits_completed_subjects?.length || 0,
        ],
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  });

  const getOverallChartData = () => {
    const overallKPIs = getOverallKPIs();

    return {
      labels: [
        "Pre-Screening",
        "Screening Failed",
        "Continuing (Active)",
        "Withdrew",
        "Terminated",
        "All Visits Completed",
      ],
      datasets: [
        {
          backgroundColor: CHART_COLORS,
          data: [
            overallKPIs[1]?.count || 0,
            overallKPIs[2]?.count || 0,
            overallKPIs[3]?.count || 0,
            overallKPIs[4]?.count || 0,
            overallKPIs[5]?.count || 0,
            overallKPIs[6]?.count || 0,
          ],
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    };
  };

  return (
    <Box>
      {isSummaryTrialLoading ? (
        <Loader />
      ) : (
        <>
          {/* Header Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
              {canExportStudyReport && (
                <CustomButton
                  size="small"
                  onClick={() => setOpenExportReportModal(true)}
                  data-testid="export-study-report-btn"
                  startIcon={<DownloadIcon fontSize="small" />}
                >
                  Export Study
                </CustomButton>
              )}

              {/* Lock Trial — lifecycle admin, Active trial */}
              {isLifecycleAdmin && trialStatus === "Active" && (
                <CustomButton
                  size="small"
                  onClick={handleMarkCompleteClick}
                  disabled={isPreviewFetching}
                  data-testid="mark-complete-btn"
                >
                  {isPreviewFetching ? <CircularProgress size={16} /> : "Lock Trial"}
                </CustomButton>
              )}

              {/* Unlock Trial — lifecycle admin, Completed trial */}
              {isLifecycleAdmin && trialStatus === "Completed" && (
                <CustomButton
                  size="small"
                  onClick={() => setOpenMarkActiveDialog(true)}
                  data-testid="mark-active-btn"
                >
                  Unlock Trial
                </CustomButton>
              )}

              {/* Archive — lifecycle admin, Completed trial */}
              {isLifecycleAdmin && trialStatus === "Completed" && (
                <CustomButton
                  size="small"
                  onClick={() => setOpenArchiveDialog(true)}
                  disabled={isArchiving}
                  data-testid="archive-trial-btn"
                >
                  Archive
                </CustomButton>
              )}

              {/* Unarchive — Super Admin only, Archived trial */}
              {isSuperAdmin && trialStatus === "Archived" && (
                <CustomButton
                  size="small"
                  onClick={() => setOpenUnarchiveDialog(true)}
                  data-testid="unarchive-trial-btn"
                >
                  Unarchive
                </CustomButton>
              )}

              {isSuperAdmin && (
                <CustomButton
                  size="small"
                  onClick={() => setOpenConfirmDeleteTrialDialog(true)}
                  data-testid="delete-trial-btn"
                >
                  Delete Trial
                </CustomButton>
              )}
            </Stack>
          </Box>

          {/* Content Section */}
          {summaryTrial?.has_trial_id_setting ? (
            <Grid container spacing={3} justifyContent="center">
              {/* Overall KPIs Card */}
              {summaryTrial?.sites?.length > 1 && (
                <Grid item xs={12}>
                  <StyledCard>
                    <CardContent>
                      <Typography
                        variant="h6"
                        component="h2"
                        gutterBottom
                        textAlign="center"
                        fontWeight="bold"
                        color="#EE295D"
                      >
                        Overall Trial KPIs
                      </Typography>

                      {getOverallLastEnrolled() && (
                        <Typography
                          variant="body2"
                          textAlign="center"
                          color="text.secondary"
                          mb={1}
                        >
                          Last Subject Enrolled:{" "}
                          {dayjs
                            .utc(getOverallLastEnrolled().date)
                            .local()
                            .format("MMM D, YYYY h:mm A")}
                          {getOverallLastEnrolled().participant_id &&
                            ` (Subject ID: ${getOverallLastEnrolled().participant_id})`}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={3} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <Stack>
                            {getOverallKPIs()
                              .filter((item) => item.visible !== false)
                              .map((item, idx) => (
                                <Stack
                                  key={idx}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  onClick={() =>
                                    item.clickable && item.data
                                      ? handleShowFilterPatientPopUp(item.data)
                                      : null
                                  }
                                  sx={{
                                    cursor: item.clickable ? "pointer" : "default",
                                    py: 0.75,
                                    px: 1,
                                    borderRadius: 1,
                                    "&:hover": item.clickable
                                      ? { backgroundColor: "action.hover" }
                                      : {},
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: KPI_COLORS[item.label],
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography variant="body2">{item.label}</Typography>
                                  </Stack>

                                  <Typography
                                    sx={{
                                      fontSize: "1.0rem",
                                      fontWeight: 700,
                                      color: item.clickable ? "#EE295D" : "text.primary",
                                      ml: 2,
                                    }}
                                  >
                                    {item.count}
                                  </Typography>
                                </Stack>
                              ))}
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 300,
                          }}
                        >
                          <Doughnut
                            data={getOverallChartData()}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              cutout: "60%",
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    label: function (context) {
                                      const label = context.label || "";
                                      const value = context.parsed || 0;
                                      return `${label}: ${value}`;
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </Box>
                      </Stack>

                      {!isDiscrepancyError && (
                        <EcrfDiscrepancySection
                          ecrfDiscrepancies={getOverallEcrfDiscrepancies()}
                          loading={isDiscrepancyLoading}
                          onClick={() =>
                            handleShowEcrfDiscrepancyPopUp(getOverallEcrfDiscrepancies())
                          }
                        />
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid>
              )}

              {summaryTrial?.sites?.map((site, index) => (
                <Grid item xs={12} lg={6} key={index}>
                  <StyledCard>
                    <CardContent>
                      <Typography
                        variant="h6"
                        component="h2"
                        gutterBottom
                        textAlign="center"
                        fontWeight="bold"
                        color="#EE295D"
                      >
                        Site {site?.site_number}
                      </Typography>

                      {site?.last_subject_enrolled_date && (
                        <Typography
                          variant="body2"
                          textAlign="center"
                          color="text.secondary"
                          mb={1}
                        >
                          Last Subject Enrolled:{" "}
                          {dayjs
                            .utc(site.last_subject_enrolled_date.date)
                            .local()
                            .format("MMM D, YYYY h:mm A")}
                          {site.last_subject_enrolled_date.participant_id &&
                            ` (Subject ID: ${site.last_subject_enrolled_date.participant_id})`}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={3} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <Stack>
                            {getPatientData(site).map((item, idx) => (
                              <Stack
                                key={idx}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                onClick={() =>
                                  item.clickable && item.data
                                    ? handleShowFilterPatientPopUp(item.data)
                                    : null
                                }
                                sx={{
                                  cursor: item.clickable ? "pointer" : "default",
                                  py: 0.75,
                                  px: 1,
                                  borderRadius: 1,
                                  "&:hover": item.clickable
                                    ? { backgroundColor: "action.hover" }
                                    : {},
                                }}
                              >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      backgroundColor: KPI_COLORS[item.label],
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography variant="body2">{item.label}</Typography>
                                </Stack>

                                <Typography
                                  sx={{
                                    fontSize: "1.0rem",
                                    fontWeight: 700,
                                    color: item.clickable ? "#EE295D" : "text.primary",
                                    ml: 2,
                                  }}
                                >
                                  {item.count}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 300,
                          }}
                        >
                          <Doughnut
                            data={getChartData(site)}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              cutout: "60%",
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    label: function (context) {
                                      const label = context.label || "";
                                      const value = context.parsed || 0;
                                      return `${label}: ${value}`;
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </Box>
                      </Stack>

                      {!isDiscrepancyError && (
                        <EcrfDiscrepancySection
                          ecrfDiscrepancies={getSiteEcrfDiscrepancies(site)}
                          loading={isDiscrepancyLoading}
                          onClick={() =>
                            handleShowEcrfDiscrepancyPopUp(getSiteEcrfDiscrepancies(site))
                          }
                        />
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                There is no Site ID for this Trial
              </Typography>
            </Paper>
          )}

          {/* Modals */}
          {filterPatientPopUp && (
            <FilterPatientPopUp
              setFilterPatientPopUp={setFilterPatientPopUp}
              filterPatient={filterPatient}
              trialId={_id_}
              trialStructure={summaryTrial?.trial_structure}
            />
          )}

          {ecrfDiscrepancyPopUp && (
            <EcrfDiscrepancyPopUp
              setEcrfDiscrepancyPopUp={setEcrfDiscrepancyPopUp}
              ecrfDiscrepancies={ecrfDiscrepancyData}
              trialId={_id_}
              trialStructure={summaryTrial?.trial_structure}
            />
          )}
        </>
      )}

      {/* Delete Trial Dialog */}
      <Dialog
        open={openConfirmDeleteTrialDialog}
        onClose={() => setOpenConfirmDeleteTrialDialog(false)}
        aria-labelledby="confirm-delete-dialog"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-delete-dialog">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this trial?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteTrialDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeletePatients}
            color="error"
            variant="contained"
            data-testid="confirm-delete-trial-btn"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock Trial Preview Dialog */}
      <Dialog
        open={openPreviewDialog}
        onClose={() => setOpenPreviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lock Trial</DialogTitle>
        <DialogContent>
          {previewData?.incomplete_patient_count > 0 ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                {previewData.incomplete_patient_count} patient(s) still have a subject status of
                Continuing or Pre-Screening.
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 2,
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                    Subject ID
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                    Status
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 160, overflowY: "auto" }}>
                  {previewData.incomplete_patients.map((patient, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      onClick={
                        isCro
                          ? undefined
                          : () => {
                              window.open(
                                summaryTrial?.trial_structure === "visit_based"
                                  ? `/patient/${patient.patient_id}/ecrf?trial=${_id_}`
                                  : `/patient/${patient.patient_id}/visit_status?trial=${_id_}`,
                                "_blank"
                              );
                            }
                      }
                      sx={{
                        px: 2,
                        py: 0.75,
                        cursor: isCro ? "default" : "pointer",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": isCro ? {} : { bgcolor: "action.hover" },
                        transition: "background-color 0.15s",
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {patient.site_participant_id}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }}>
                        {patient.patient_status}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </>
          ) : null}

          {previewData?.incomplete_patient_count > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="incomplete-status-label">
                Which status would you like to assign to these patients?
              </InputLabel>
              <Select
                labelId="incomplete-status-label"
                value={selectedIncompleteStatus}
                label="Which status would you like to assign to these patients?"
                onChange={(e) => setSelectedIncompleteStatus(e.target.value)}
                SelectDisplayProps={{ "data-testid": "incomplete-status-trigger" }}
              >
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Withdrew">Withdrew</MenuItem>
                <MenuItem value="Terminated">Terminated</MenuItem>
              </Select>
            </FormControl>
          )}

          <Typography color="warning.main" fontWeight={600}>
            Are you sure you want to lock this trial?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMarkComplete}
            variant="contained"
            data-testid="confirm-mark-complete-btn"
            disabled={
              (previewData?.incomplete_patient_count > 0 && !selectedIncompleteStatus) ||
              markCompleteMutation.isPending
            }
          >
            {markCompleteMutation.isPending ? <CircularProgress size={16} /> : "Lock Trial"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Trial Dialog */}
      <Dialog
        open={openMarkActiveDialog}
        onClose={() => setOpenMarkActiveDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unlock Trial</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to unlock this trial?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMarkActiveDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMarkActive}
            variant="contained"
            disabled={markActiveMutation.isPending}
          >
            {markActiveMutation.isPending ? <CircularProgress size={16} /> : "Unlock Trial"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog
        open={openArchiveDialog}
        onClose={() => setOpenArchiveDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Archive Trial</DialogTitle>
        <DialogContent>
          <Typography>
            This will generate archive reports for all trial data and make the trial read-only. This
            action runs in the background and may take a few minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenArchiveDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmArchive}
            variant="contained"
            disabled={archiveMutation.isPending}
            data-testid="confirm-archive-trial-btn"
          >
            {archiveMutation.isPending ? <CircularProgress size={16} /> : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      <ExportStudyReportModal
        open={openExportReportModal}
        onClose={() => setOpenExportReportModal(false)}
        trialId={_id_}
      />

      {/* Archiving in-progress modal — non-dismissable while archiveTaskId is set */}
      <Dialog open={isArchiving} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Archiving Trial</DialogTitle>
        <DialogContent>
          <Typography color="error" sx={{ mb: 2 }}>
            Do not close or refresh this browser tab/window until archiving is complete.
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            {reportsArchiveState === "success" ? (
              <Typography variant="body2">✓</Typography>
            ) : reportsArchiveState === "failure" ? (
              <Typography variant="body2" color="error">
                ✕
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
            <Typography variant="body2">
              {reportsArchiveState === "failure"
                ? "Generating backend reports failed"
                : "Generating backend reports…"}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            {ecrfArchiveState === "success" ? (
              <Typography variant="body2">✓</Typography>
            ) : ecrfArchiveState === "failure" ? (
              <Typography variant="body2" color="error">
                ✕
              </Typography>
            ) : (
              <CircularProgress size={16} />
            )}
            <Typography variant="body2">
              {ecrfArchiveState === "failure"
                ? "Generating eCRF PDF zip failed"
                : `Generating eCRF PDFs (${ecrfArchiveProgress.current}/${ecrfArchiveProgress.total})…`}
            </Typography>
          </Stack>

          {finalizeArchiveMutation.isPending && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Finalizing archive…</Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Unarchive Dialog */}
      <Dialog
        open={openUnarchiveDialog}
        onClose={() => setOpenUnarchiveDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unarchive Trial</DialogTitle>
        <DialogContent>
          <Typography>This will revert the trial to Completed status. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnarchiveDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUnarchive}
            variant="contained"
            disabled={unarchiveMutation.isPending}
            data-testid="confirm-unarchive-trial-btn"
          >
            {unarchiveMutation.isPending ? <CircularProgress size={16} /> : "Unarchive"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SummaryTrial;
